// Promos: a reference catalogue of the in-app nudges we show players. For each one
// we render the REAL component (so the preview can't drift from production) next to
// a plain-English breakdown of when it fires and how it's configured.
import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Sheet } from '../../components/ui/Sheet';
import {
  MiniLeaguePromoContent,
  SEEN_KEY,
  SHOW_DELAY_MS
} from '../../components/ui/MiniLeaguePromo';
import { PageTitle, Panel, Pill, StatCard, useAdminData } from './parts';

const SAMPLE_LEAGUE = 'Premier League';

/** Every condition that must hold for the Mini League promo to fire (mirrors the
 *  gating in MiniLeaguePromo). Kept here as docs so the dashboard reads like a spec. */
const CONDITIONS = [
  'Signed in with a completed profile',
  'Has joined at least one league',
  'Has made their picks — an entry this round, or earlier settled play',
  'Not already in a mini league',
  "Hasn't seen this promo before (tracked per account)"
];

export default function Promos() {
  const [live, setLive] = useState(false);
  const { data } = useAdminData();

  // The live funnel for this nudge. Guarded so a snapshot cached from before the
  // tracking shipped (no `promos` field) renders as zeros rather than crashing.
  const f = data.promos?.miniLeague ?? { shown: 0, create: 0, join: 0, dismiss: 0 };
  const acted = f.create + f.join;
  const noAction = Math.max(0, f.shown - f.create - f.join - f.dismiss);
  const pct = (n: number) => (f.shown > 0 ? `${Math.round((n / f.shown) * 100)}%` : '—');

  return (
    <>
      <PageTitle title="Promos" sub="In-app nudges we show players — what they look like, when they fire, and how they're landing." />

      {/* Live funnel — folded from each player's users/{uid}.miniLeaguePromo record. */}
      <div className="mb-5">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-bold tracking-tight">Mini League promo · performance</h2>
          <span className="text-[12px] font-semibold text-ink2">
            {f.shown > 0 ? `${pct(acted)} tapped a CTA` : 'No impressions yet'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Shown" value={f.shown.toLocaleString()} sub="impressions" />
          <StatCard label="Tapped create" value={f.create.toLocaleString()} sub={`${pct(f.create)} of shown`} accent />
          <StatCard label="Tapped join" value={f.join.toLocaleString()} sub={`${pct(f.join)} of shown`} />
          <StatCard label="Dismissed" value={f.dismiss.toLocaleString()} sub={`${pct(f.dismiss)} of shown`} />
        </div>
        <p className="mt-2.5 text-[12px] text-ink2">
          Outcomes are CTA taps (the create/join flow opens), not completed leagues — track those under{' '}
          <Mono>Mini Leagues</Mono>. {noAction > 0 ? `${noAction.toLocaleString()} closed without choosing. ` : ''}
          Counted once per account.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Live, to-scale preview (the real component) */}
        <Panel
          title="Mini League promo"
          action={
            <button
              type="button"
              onClick={() => setLive(true)}
              className="tap text-[12px] font-bold text-accent-ink"
            >
              Play it ↗
            </button>
          }
          bodyClassName="bg-canvas p-5 md:p-6"
        >
          <div className="relative mx-auto w-full max-w-[400px] select-none overflow-hidden rounded-[28px] bg-surface-top px-5 pb-7 pt-1 shadow-card ring-1 ring-black/5">
            <div className="flex justify-center pb-1 pt-3">
              <div className="h-[5px] w-[44px] rounded-full bg-black/15" />
            </div>
            {/* Snapshot only — the live, interactive version is behind "Play it". */}
            <div className="pointer-events-none">
              <MiniLeaguePromoContent
                leagueName={SAMPLE_LEAGUE}
                onClose={() => {}}
                onCreate={() => {}}
                onJoin={() => {}}
              />
            </div>
          </div>
        </Panel>

        {/* Spec: triggers + settings */}
        <div className="flex flex-col gap-4">
          <Panel title="When it shows">
            <div className="px-4 py-3 md:px-5">
              <p className="mb-3 text-[12px] text-ink2">A one-time nudge — fires only when all of these are true:</p>
              <ul className="flex flex-col gap-2.5">
                {CONDITIONS.map((c) => (
                  <li key={c} className="flex items-start gap-2.5">
                    <span className="mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-accent-wash text-accent-ink">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="text-[13px] text-ink">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>

          <Panel title="Settings">
            <div className="divide-y divide-black/5">
              <Row label="Frequency">
                <Pill tone="accent">Once per account</Pill>
              </Row>
              <Row label="Marked seen">The moment it opens (it never nags)</Row>
              <Row label="Delay after screen settles">
                <Mono>{SHOW_DELAY_MS} ms</Mono>
              </Row>
              <Row label="Tracked on">
                <Mono>users/&#123;uid&#125;.miniLeaguePromo</Mono>
              </Row>
              <Row label="Local mirror">
                <Mono>localStorage</Mono> · <Mono>{SEEN_KEY}</Mono>
              </Row>
              <Row label="Mounted in">AppChrome — any signed-in screen</Row>
              <Row label="Skipped on">
                <Mono>/…/picks</Mono> · <Mono>/leagues/*</Mono> · <Mono>/admin/*</Mono>
              </Row>
              <Row label="Primary action">
                Create a Mini League → <Mono>/leagues/new</Mono>
              </Row>
              <Row label="Secondary action">
                Join a league → <Mono>/leagues/join</Mono>
              </Row>
              <Row label="Dismiss">✕ button · backdrop tap · drag down</Row>
            </div>
          </Panel>
        </div>
      </div>

      {/* "Play it" — the actual sheet, exactly as a player sees it (slide-up,
          backdrop, drag-to-dismiss). Buttons just close here (no real navigation). */}
      <Sheet
        open={live}
        onClose={() => setLive(false)}
        ariaLabel="Mini league promo preview"
        className="px-5 pb-7 pt-1"
      >
        <MiniLeaguePromoContent
          leagueName={SAMPLE_LEAGUE}
          onClose={() => setLive(false)}
          onCreate={() => setLive(false)}
          onJoin={() => setLive(false)}
        />
      </Sheet>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-5">
      <span className="shrink-0 text-[13px] font-semibold text-ink2">{label}</span>
      <span className="text-right text-[13px] font-medium text-ink">{children}</span>
    </div>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-md bg-fill px-1.5 py-0.5 font-mono text-[12px] font-semibold text-ink">
      {children}
    </code>
  );
}
